import { a as __rest } from '../../tslib.es6.esm.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { lazy, forwardRef, useState, useRef, useEffect, Suspense } from 'react';
import { Spinner } from '../Spinner/Spinner.esm.js';
import { aiResponseStyles } from './AIResponse.styles.esm.js';
import { cn } from '../../utils/cn.esm.js';

// Lazy load the MarkdownRenderer to avoid loading markdown libraries on initial page load
const MarkdownRenderer = lazy(() => import('./dep-components/index.esm.js').then((module) => ({
    default: module.MarkdownRenderer,
})));
/**
 * The AIResponse component renders AI-generated markdown content with proper typography and spacing.
 * It supports your design system components for headings, links, images, and dividers,
 * while falling back to styled HTML for tables, lists, and quotes.
 */
const AIResponse = forwardRef((_a, ref) => {
    var { content, children, inverse = false, className, streaming, onStreamingComplete, onStreamingProgress } = _a, props = __rest(_a, ["content", "children", "inverse", "className", "streaming", "onStreamingComplete", "onStreamingProgress"]);
    const { container } = aiResponseStyles({ inverse });
    const [liveRegionMessage, setLiveRegionMessage] = useState("");
    const timeoutRef = useRef(null);
    const isStreamingRef = useRef(false);
    // Handle streaming status announcements
    useEffect(() => {
        if (streaming === null || streaming === void 0 ? void 0 : streaming.enabled) {
            isStreamingRef.current = true;
            // Announce that response is generating
            setLiveRegionMessage("AI response is generating…");
            // Set a timeout to announce if still generating after 10 seconds
            timeoutRef.current = setTimeout(() => {
                if (isStreamingRef.current) {
                    setLiveRegionMessage("AI response is still generating…");
                }
            }, 10000);
            return () => {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
            };
        }
    }, [streaming === null || streaming === void 0 ? void 0 : streaming.enabled]);
    // Handle streaming completion
    const handleStreamingComplete = () => {
        isStreamingRef.current = false;
        setLiveRegionMessage("AI response complete");
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        if (onStreamingComplete) {
            onStreamingComplete();
        }
    };
    // If content is provided, render as markdown
    if (content) {
        return (jsxs("div", Object.assign({ className: cn(container(), className) }, props, { ref: ref, children: [jsx("h2", { className: "iris:sr-only", children: "AI response:" }), jsx("div", { "aria-live": "assertive", "aria-atomic": "true", className: "iris:sr-only", role: "status", children: liveRegionMessage }), jsx(Suspense, { fallback: jsx(Spinner, { size: "md" }), children: jsx(MarkdownRenderer, { content: content, streaming: streaming, onStreamingComplete: handleStreamingComplete, onStreamingProgress: onStreamingProgress, inverse: inverse }) })] })));
    }
    // Fallback to children for legacy support
    return (jsxs("div", Object.assign({ className: cn(container(), className) }, props, { ref: ref, children: [jsx("h2", { className: "iris:sr-only", children: "AI response:" }), jsx("div", { "aria-live": "assertive", "aria-atomic": "true", className: "iris:sr-only", role: "status", children: liveRegionMessage }), children] })));
});
AIResponse.displayName = "AIResponse";

export { AIResponse };
